import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types";
import { forkJoin, map, Observable } from "rxjs";
import { ProgramDimensionDto } from "../../../../core/models/program/programDimensionDto";
import { AddProgramDto } from "../../../../core/models/program/addProgramDto";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { ProgramBCDto } from "../../../../core/models/program/programBCListDto";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { LoaderService } from "../../../../core/services/loader.service";
import { ProgramsService } from "../../../../core/services/programs.service";
import { UserRole } from "../../../../shared/enums/userRoles.enum";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { MsalApiService } from "../../../../core/services/msal-api.service";
import { ProgramHeaderComponent } from "../program-header/program-header.component";
import { ResultForkJoinDto } from "../../../../core/models/program/resultForkJoinDto";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { GetProgramDetailsEmailDto } from "../../../../core/models/program/getProgramDetailsForEmailDto";
import { GetUserDto } from "../../../../core/models/users/users/getUserDto";

@UntilDestroy()
@Component({
  selector: "jni-add-program",
  templateUrl: "./add-program.component.html",
  styleUrls: ["./add-program.component.scss"],
  imports: [
    ProgramHeaderComponent,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
})
export class AddProgramComponent implements OnInit {
  showSmallLoader = false;
  programForm!: FormGroup;
  isReadOnly = false;
  bcList: ProgramBCDto[] = [];
  //validationMessages: any= {};
  programDto = {} as AddProgramDto;
  threePlList: DropDownDto[] = [];
  platfromList: DropDownDto[] = [];
  facilitatorList: DropDownDto[] = [];
  programDimensions: ProgramDimensionDto[] = [];
  isAdminOrFinance = false;
  result$!: Observable<ResultForkJoinDto>;
  programId = "";
  userInfo: GetUserDto = {} as GetUserDto;
  validateLoginData = {} as ValidateLogin;
  isEditDimension = false;
  mode = "Add";

  constructor(
    private _fb: FormBuilder,
    private _programService: ProgramsService,
    private _loaderService: LoaderService,
    private _toastService: JniToastrService,
    private route: Router,
    private _userService: UserManagementService,
    private _msalApisService: MsalApiService
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    this.getAllValues();
  }

  getAllValues() {
    this._loaderService.setLoaderState(true);
    this.result$ = forkJoin({
      getWhoISupport: this._programService.getWhoISupport(),
      getProgramDimensions: this._programService.getProgramDimensions(),
      getFacilitatorList: this._programService.getFacilitatorList(),
      getThreePLList: this._programService.getThreePLList(),
      getPlatformList: this._programService.getPlatformList(),
      validateLogin: this._userService.validateLogin(),
    }).pipe(
      map(res => {
        return {
          whoISupport: res.getWhoISupport.data,
          programDimensions: res.getProgramDimensions.data,
          facilitatorList: res.getFacilitatorList.data,
          threePLList: res.getThreePLList.data,
          platformList: res.getPlatformList.data,
          validateLogin: res.validateLogin.data,
        } as unknown as ResultForkJoinDto;
      })
    );

    this.result$.subscribe((res: ResultForkJoinDto) => {
      const uniqueSupport = res.whoISupport.filter(
        (item: ProgramBCDto, index: number, self: ProgramBCDto[]) =>
          index ===
          self.findIndex(t => t.personnelNumber === item.personnelNumber)
      );
      this.bcList = uniqueSupport.map((item: ProgramBCDto) => ({
        ...item,
        displayValue: `${item.personnelNumber} : ${item.projectName}`,
      }));
      this.facilitatorList = res.facilitatorList;
      this.threePlList = res.threePLList;
      this.programDimensions = res.programDimensions;
      this.platfromList = res.platformList;
      this.validateLoginData = res.validateLogin;
      if (res.validateLogin.userRoleId == UserRole.Admin) {
        this.isAdminOrFinance = true;
        this.isEditDimension = true;
      } else {
        this.isAdminOrFinance = false;
        this.isEditDimension = false;
      }
      this._loaderService.setLoaderState(false);
    });
  }

  private initializeForm() {
    this.programForm = this._fb.group({
      id: [""],
      name: ["", Validators.required],
      brandingConsultantD365: ["", Validators.required],
      office: [""],
      description: [""],
      ecomOnboard: [false],
      inStoreCC: [false],
      usesWebsite: [false],
      clienttOwnInv: [false],
      nadelOwnInv: [false],
      intl: [false],
      website1: [""],
      website2: [""],
      dimensionD365: [""],
      uses3PL: [false],
      micro: [false],
      threePL1Id: [""],
      threePLOther: [""],
      avaTaxIntegration: [false],
      webPlatId: [""],
      inStoreCCProvider: [""],
      isApproved: [false],
      isDisabled: [false],
      isClientLock: [false],
    });
  }

  getLoaderStatus(status: boolean) {
    this.showSmallLoader = status;
  }

  programFieldMapper(data: AddProgramDto) {
    this.programDto.name = data.name;
    this.programDto.dimensionD365 = data.dimensionD365;
    this.programDto.office = data.office;
    this.programDto.description = data.description;
    this.programDto.brandingConsultantD365 = data.brandingConsultantD365;
    this.programDto.nadelOwnInv = data.nadelOwnInv;
    this.programDto.clientOwnInv = data.clientOwnInv;
    this.programDto.uses3PL = data.uses3PL;
    this.programDto.threePL1Id = data.threePL1Id ?? 0;
    this.programDto.usesWebsite = data.usesWebsite;
    this.programDto.webPlatId = data.webPlatId ?? 0;
    this.programDto.threePLOther = data.threePLOther;
    this.programDto.inStoreCC = data.inStoreCC;
    this.programDto.inStoreCCProvider = data.inStoreCCProvider ?? 0;
    this.programDto.intl = data.intl;
    this.programDto.micro = data.micro;
    this.programDto.ecomOnboard = data.ecomOnboard;
    this.programDto.website1 = data.website1;
    this.programDto.website2 = data.website2;
    this.programDto.isApproved = data.isApproved;
    this.programDto.isDisabled = data.isDisabled;
    this.programDto.isClientLock = data.isClientLock;
    this.programDto.avaTaxIntegration = data.avaTaxIntegration;
  }

  onSubmit() {
    const programFormValues = this.programForm.getRawValue();
    this._loaderService.setLoaderState(true);

    this.programFieldMapper(programFormValues);

    if (this.programForm.invalid) {
      this._toastService.error("Error", "Error adding program");
    }
    this._programService
      .createProgram(this.programDto)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.data != null) {
            this.getProgramEmailContent((res.data.id ?? "").toString());
            this._toastService.success("Success", "Program added successfully");
          } else {
            this._toastService.error(
              "Error",
              res.message ?? "Error adding program"
            );
          }
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastService.error(
            "Error",
            res.message ?? "Error adding program"
          );
        },
      });
  }

  setFormData($event: FormGroup) {
    this.programForm = $event;
  }

  getMyProfile() {
    this._programService
      .GetMyProfile()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.status.toLowerCase() == "success") {
            this.userInfo = res.data;
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  getProgramEmailContent(programId: string) {
    if (this.validateLoginData) {
      this._programService
        .GetProgramEmailDetails(programId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            this._loaderService.setLoaderState(false);
            if (res.data != null) {
              this.sendProgramCreationEmail(res.data);
            } else {
              this._toastService.error(
                "Error",
                res.message ?? "Error sending program email"
              );
            }

            const url = "/programs";
            this.route.navigate([url]);
          },
          error: res => {
            this._loaderService.setLoaderState(false);
            this._toastService.error(
              "Error",
              res.message ?? "Error sending program email"
            );
          },
        });
    }
  }

  sendProgramCreationEmail(details: GetProgramDetailsEmailDto) {
    const toRecipients: MicrosoftGraph.Recipient[] = [];
    details.receiverEmail.forEach(x => {
      if (x !== "") {
        toRecipients.push({
          emailAddress: {
            address: x,
            name: x || undefined,
          },
        });
      }
    });

    const sender: MicrosoftGraph.Recipient = {
      emailAddress: {
        address: this.userInfo.sendEmailAs,
        name: this.userInfo.displayEmailAs,
      },
    };

    let mailMessage: MicrosoftGraph.Message = {};

    mailMessage = {
      subject: details.subject,
      body: {
        contentType: "html",
        content: details.html,
      },
      toRecipients: toRecipients,
      sender: sender,
    };

    const mail = {
      message: mailMessage,
    };
    this._msalApisService.sendMail(mail).subscribe({
      next: () => {
        const url = `programs`;
        this.route.navigate([url]);
        this._loaderService.setLoaderState(false);
      },
      error: () => {
        this._loaderService.setLoaderState(false);
      },
    });
  }

  navigateToProgramListing() {
    this.route.navigate(["/programs"]);
  }
}
